import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { susunSasaran } from "@/lib/surveiKepnakes";

const persen = (v) => (v === null || v === undefined ? "–" : `${v.toLocaleString("id-ID")}%`);

// Cakupan periode Kepnakes (PR #112, Super Admin): jumlah responden total & per institusi (angka saja, aturan sama dgn
// Cakupan Kedokteran) + pengaturan periode: pertanyaan Jenis Kelamin (aktif/nonaktif) & sasaran per institusi (opsional;
// partisipasi = responden / sasaran, tampil hanya bila sasaran diisi). Keduanya boleh diubah selama periode dibuka.
// `bacaSaja` (Admin UFP): angka & pengaturan hanya ditampilkan, tanpa kontrol ubah (server menolak 403).
export default function SurveiCakupanKepnakes({ periodeId, onBerubah, bacaSaja = false }) {
    const [data, setData] = useState(null);
    const [isian, setIsian] = useState({});
    const [sibuk, setSibuk] = useState(false);
    const muat = useCallback(() => api.get(`/survei/periode/${periodeId}/cakupan`)
        .then((r) => {
            setData(r.data);
            setIsian(Object.fromEntries(r.data.baris.filter((b) => b.sasaran).map((b) => [b.institusi_id, String(b.sasaran)])));
        })
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))), [periodeId]);
    useEffect(() => { setData(null); muat(); }, [muat]);

    const simpan = async (body, pesan) => {
        setSibuk(true);
        try {
            await api.put(`/survei/periode/${periodeId}/pengaturan-kepnakes`, body);
            toast.success(pesan);
            await muat();
            onBerubah?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setSibuk(false);
    };
    const { sasaran, galat } = susunSasaran(isian);

    if (!data) return <p className="py-6 text-sm text-slate-500">Memuat…</p>;
    return (
        <div className="space-y-4" data-testid="survei-cakupan-kepnakes">
            <div className="grid gap-3 md:grid-cols-2">
                <Card className="p-4">
                    <div className="text-xs font-semibold uppercase text-slate-500">Responden Kepnakes</div>
                    <div className="text-2xl font-extrabold text-slate-900" data-testid="survei-cakupan-kepnakes-total">{data.total.responden}</div>
                    <div className="text-xs text-slate-500">Tanpa verifikasi identitas; satu orang bisa mengisi lebih dari sekali.</div>
                </Card>
                {bacaSaja ? (
                    <Card className="p-4">
                        <div className="text-xs font-semibold uppercase text-slate-500">Pertanyaan Jenis Kelamin</div>
                        <div className="text-sm font-semibold text-slate-900" data-testid="survei-jk-teks">{data.jenis_kelamin ? "Ditanyakan" : "Tidak ditanyakan"}</div>
                    </Card>
                ) : <Card className="flex items-start justify-between gap-3 p-4">
                    <div>
                        <Label htmlFor="survei-jk-aktif" className="font-semibold">Tanyakan Jenis Kelamin</Label>
                        <p className="text-xs text-slate-500">Berlaku untuk pengisian berikutnya pada periode ini.</p>
                    </div>
                    <Switch id="survei-jk-aktif" data-testid="survei-jk-aktif" checked={data.jenis_kelamin} disabled={sibuk}
                            onCheckedChange={(v) => simpan({ jenis_kelamin: v }, v ? "Jenis Kelamin ditanyakan" : "Jenis Kelamin tidak ditanyakan")} />
                </Card>}
            </div>
            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="compact w-full" data-testid="survei-cakupan-kepnakes-tabel">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                                <th>Institusi</th><th className="text-right">Responden</th><th className="w-40 text-right">Sasaran</th>
                                <th className="pr-4 text-right">Partisipasi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.baris.length === 0 ? (
                                <tr><td colSpan={4} className="py-8 text-center text-slate-400">Belum ada institusi.{bacaSaja ? "" : " Tambahkan di tab Master Kepnakes."}</td></tr>
                            ) : data.baris.map((b) => (
                                <tr key={b.institusi_id || "-"} className="border-t border-slate-200">
                                    <td className="font-semibold text-slate-800">{b.nama}{!b.aktif && <span className="ml-2 text-xs font-normal text-slate-500">(nonaktif)</span>}</td>
                                    <td className="text-right tabular-nums">{b.responden}</td>
                                    <td className="text-right">
                                        {bacaSaja && <span className="tabular-nums" data-testid={`survei-sasaran-teks-${b.institusi_id}`}>{b.sasaran ?? "–"}</span>}
                                        {!bacaSaja && b.institusi_id && b.nama !== "(tidak dikenal)" && (
                                            <Input aria-label={`Sasaran ${b.nama}`} data-testid={`survei-sasaran-${b.institusi_id}`} inputMode="numeric"
                                                   className="ml-auto h-8 w-28 text-right" value={isian[b.institusi_id] || ""}
                                                   onChange={(e) => setIsian((x) => ({ ...x, [b.institusi_id]: e.target.value }))} />
                                        )}
                                    </td>
                                    <td className="pr-4 text-right tabular-nums">{persen(b.persen)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {!bacaSaja && <div className="flex flex-wrap items-center justify-end gap-3 border-t p-3">
                    {galat && <p className="text-xs text-rose-600" role="alert">{galat}</p>}
                    <Button size="sm" disabled={!!galat || sibuk || data.baris.length === 0} onClick={() => simpan({ sasaran_per_institusi: sasaran }, "Sasaran disimpan")}
                            className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]" data-testid="survei-sasaran-simpan">Simpan sasaran</Button>
                </div>}
            </Card>
            {!bacaSaja && <p className="text-xs text-slate-500">Sasaran diisi manual (jumlah peserta didik yang diharapkan mengisi). Kosongkan untuk menghapus sasaran.</p>}
        </div>
    );
}
